/**
 * @module features/devices/components/DisplayModuleFields
 * @description Type-specific inputs for `display_module` devices. Controlled by
 * the parent react-hook-form instance so the register modal can swap field
 * groups per device type without owning their markup.
 */
import * as React from "react";
import { FieldErrors, UseFormRegister } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { DeviceCreateFormValues, OS_HASH_REGEX } from "../types";

interface DisplayModuleFieldsProps {
  register: UseFormRegister<DeviceCreateFormValues>;
  errors: FieldErrors<DeviceCreateFormValues>;
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p role="alert" className="text-[11px] text-destructive">
      {message}
    </p>
  );
}

export function DisplayModuleFields({ register, errors }: DisplayModuleFieldsProps) {
  return (
    <fieldset className="space-y-4 rounded-xl border border-border/70 bg-muted/20 p-4">
      <legend className="px-1.5 text-xs font-semibold text-foreground">
        Display module details
      </legend>

      <div className="space-y-1">
        <label htmlFor="os_name" className="text-xs font-medium text-foreground block">
          OS name <span className="text-destructive">*</span>
        </label>
        <Input
          id="os_name"
          placeholder="Ubuntu Core"
          aria-invalid={Boolean(errors.os_name)}
          {...register("os_name", {
            required: "OS name is required.",
            maxLength: { value: 100, message: "OS name cannot exceed 100 characters." },
            validate: (v) => v.trim().length > 0 || "OS name is required.",
          })}
          className="h-10 text-sm bg-card"
        />
        <FieldError message={errors.os_name?.message} />
      </div>

      <div className="space-y-1">
        <label htmlFor="os_version" className="text-xs font-medium text-foreground block">
          OS version <span className="text-destructive">*</span>
        </label>
        <Input
          id="os_version"
          placeholder="22.04.3-LTS"
          aria-invalid={Boolean(errors.os_version)}
          {...register("os_version", {
            required: "OS version is required.",
            maxLength: { value: 50, message: "OS version cannot exceed 50 characters." },
            validate: (v) => v.trim().length > 0 || "OS version is required.",
          })}
          className="h-10 text-sm bg-card"
        />
        <FieldError message={errors.os_version?.message} />
      </div>

      <div className="space-y-1">
        <label htmlFor="os_hash" className="text-xs font-medium text-foreground block">
          OS hash / build signature <span className="text-destructive">*</span>
        </label>
        <Input
          id="os_hash"
          placeholder="Build hash or release tag"
          autoComplete="off"
          spellCheck={false}
          aria-invalid={Boolean(errors.os_hash)}
          {...register("os_hash", {
            required: "OS hash is required.",
            maxLength: { value: 128, message: "OS hash cannot exceed 128 characters." },
            validate: (v) =>
              OS_HASH_REGEX.test(v.trim()) || "Use letters, digits and . _ : + - only.",
          })}
          className="h-10 text-sm font-mono bg-card"
        />
        <FieldError message={errors.os_hash?.message} />
      </div>
    </fieldset>
  );
}
